import { useState, useEffect, useMemo } from 'react';
import { useStore, LeadStatus, calculateDealScore, LeadSource } from '../store/useStore';
import toast from 'react-hot-toast';
import { geocodeAddress } from '../lib/geocoding';
import { detectCarrier } from '../lib/carrier-service';
import { BarChart3, Calculator, TrendingUp, Calendar, Send, Layers } from 'lucide-react';
import { Modal } from './Modal';
import { ConfirmModal } from './ConfirmModal';
import { SaveButton } from './SaveButton';
import { fetchPropertyEstimate } from '../lib/propertyEstimate';
import { MapContainer, TileLayer, Polygon, Polyline, CircleMarker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';

function LeadFormMap({ points, onChange }: { points: [number, number][]; onChange: (points: [number, number][]) => void }) {
  const map = useMap();

  useEffect(() => {
    map.getContainer().style.cursor = 'crosshair';
    map.doubleClickZoom.disable();
    return () => {
      map.getContainer().style.cursor = '';
      map.doubleClickZoom.enable();
    };
  }, [map]);

  useMapEvents({
    click(e) {
      onChange([...points, [e.latlng.lat, e.latlng.lng]]);
    }
  });

  return (
    <>
      {points.length > 0 && (
        <Polyline positions={points} pathOptions={{ color: 'var(--t-primary)', weight: 2, dashArray: '6,6' }} />
      )}
      {points.length >= 3 && (
        <Polygon positions={points} pathOptions={{ color: 'var(--t-primary)', fillColor: 'var(--t-primary)', fillOpacity: 0.15, weight: 2 }} />
      )}
      {points.map((p, i) => (
        <CircleMarker
          key={i}
          center={p}
          radius={5}
          pathOptions={{ color: '#fff', fillColor: 'var(--t-primary)', fillOpacity: 1, weight: 2 }}
          eventHandlers={{
            click: (e) => {
              e.originalEvent.stopPropagation();
              onChange(points.filter((_, idx) => idx !== i));
            }
          }}
        />
      ))}
    </>
  );
}

interface LeadFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  leadId?: string | null;
}

export function LeadFormModal({ isOpen, onClose, leadId }: LeadFormModalProps) {
  const { leads, addLead, updateLead, team } = useStore();
  
  const editingLead = leadId ? leads.find(l => l.id === leadId) : null;
  
  const defaultData = useMemo(() => ({
    name: '', email: '', phone: '', propertyAddress: '',
    propertyType: 'single-family', estimatedValue: '', offerAmount: '',
    status: 'new', notes: '', source: 'other' as LeadSource,
    assignedTo: '',
    probability: '50', engagementLevel: '3', timelineUrgency: '3', competitionLevel: '3',
    lastSoldPrice: '', lastSoldDate: '', estimatedEquity: '', recommendedOffer: '',
    lead_type: 'seller' as 'buyer' | 'seller' | 'both',
    budget_min: '', budget_max: '',
    desired_bedrooms: '', desired_bathrooms: '',
    preferred_property_types: [] as string[],
    preferred_locations: [] as string[],
    pre_approved: false,
    lender_info: '',
    draw_polygon: null as [number, number][] | null,
  }), []);

  const [formData, setFormData] = useState(defaultData);
  const [initialData, setInitialData] = useState(defaultData);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [isSendingOffer, setIsSendingOffer] = useState(false);
  const [estimateDetails, setEstimateDetails] = useState<{ estimate: number; source: string; disclaimer: string } | null>(null);

  // Appreciation Calculation
  const appreciationPercent = useMemo(() => {
    const val = parseFloat(formData.estimatedValue);
    const sold = parseFloat(formData.lastSoldPrice);
    if (!val || !sold || sold === 0) return 0;
    return Math.round(((val - sold) / sold) * 100);
  }, [formData.estimatedValue, formData.lastSoldPrice]);

  // Lead data loading effect
  useEffect(() => {
    if (isOpen) {
      // Find the lead only when the modal opens or the ID changes
      const currentLead = leadId ? leads.find(l => l.id === leadId) : null;
      let data;
      if (currentLead) {
        data = { 
          name: currentLead.name || '', 
          email: currentLead.email || '', 
          phone: currentLead.phone || '', 
          propertyAddress: currentLead.propertyAddress || '', 
          propertyType: currentLead.propertyType || 'single-family', 
          estimatedValue: (currentLead.estimatedValue || '').toString(), 
          offerAmount: (currentLead.offerAmount || '').toString(), 
          status: currentLead.status || 'new', 
          notes: currentLead.notes || '', 
          source: (currentLead.source || 'other') as LeadSource,
          assignedTo: currentLead.assignedTo || '',
          probability: (currentLead.probability || 50).toString(), 
          engagementLevel: (currentLead.engagementLevel || 3).toString(), 
          timelineUrgency: (currentLead.timelineUrgency || 3).toString(), 
          competitionLevel: (currentLead.competitionLevel || 3).toString(),
          lastSoldPrice: (currentLead.lastSoldPrice || '').toString(),
          lastSoldDate: currentLead.lastSoldDate || '',
          estimatedEquity: (currentLead.estimatedEquity || '').toString(),
          recommendedOffer: (currentLead.recommendedOffer || '').toString(),
          lead_type: currentLead.lead_type || 'seller',
          budget_min: (currentLead.budget_min || '').toString(),
          budget_max: (currentLead.budget_max || '').toString(),
          desired_bedrooms: (currentLead.desired_bedrooms || '').toString(),
          desired_bathrooms: (currentLead.desired_bathrooms || '').toString(),
          preferred_property_types: currentLead.preferred_property_types || [],
          preferred_locations: currentLead.preferred_locations || [],
          pre_approved: !!currentLead.pre_approved,
          lender_info: currentLead.lender_info || '',
          draw_polygon: currentLead.draw_polygon || null,
        };
        
        if (currentLead.propertyAddress) {
          fetchPropertyEstimate(currentLead.propertyAddress).then(res => {
            if (res.estimate) {
              setEstimateDetails({
                estimate: res.estimate,
                source: res.source,
                disclaimer: res.disclaimer
              });
            }
          }).catch(() => {});
        }
      } else {
        data = defaultData;
        setEstimateDetails(null);
      }
      setFormData(data);
      setInitialData(data);
    }
  }, [isOpen, leadId, defaultData]); // Only reset when modal opens or lead ID changes

  const handleAddressBlur = async () => {
    const address = formData.propertyAddress;
    if (!address || address.trim().length < 5) return;
    
    try {
      const res = await fetchPropertyEstimate(address);
      if (res.estimate) {
        setFormData(prev => {
          const updates: any = {};
          if (!prev.estimatedValue) {
            updates.estimatedValue = res.estimate!.toString();
          }
          return { ...prev, ...updates };
        });
        setEstimateDetails({
          estimate: res.estimate,
          source: res.source,
          disclaimer: res.disclaimer
        });
        toast.success(`Automated value estimate: $${res.estimate.toLocaleString()}`);
      }
    } catch (err) {
      console.error('Failed to fetch property estimate:', err);
    }
  };

  // Scroll lock effect
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const isDirty = JSON.stringify(formData) !== JSON.stringify(initialData);

  const handleCloseAttempt = () => {
    if (isDirty) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  };

  const handleRecommendOffer = () => {
    const value = parseFloat(formData.estimatedValue) || 0;
    if (!value) return;
    
    // Formula: Value * 0.7 - Repairs (Mock repairs as 10% of value for now)
    const factor = formData.propertyType === 'multi-family' ? 0.75 : 0.7;
    const calcOffer = Math.round(value * (factor - 0.1));
    setFormData({ ...formData, recommendedOffer: calcOffer.toString(), offerAmount: calcOffer.toString() });
  };

  const handleSendOffer = async () => {
    if (!formData.offerAmount || !formData.name) {
      toast.error('Offer amount and lead name are required');
      return;
    }

    setIsSendingOffer(true);
    try {
      // Simulate professional document generation
      await new Promise(resolve => setTimeout(resolve, 2000));
      toast.success(`Professional offer of $${parseInt(formData.offerAmount).toLocaleString()} generated for ${formData.name}!`);
      
      // Update status to negotiating if it was new/contacted
      if (['new', 'contacted', 'qualified'].includes(formData.status)) {
        setFormData(prev => ({ ...prev, status: 'negotiating' }));
      }
      
      toast.success('Offer sent via priority courier system');
    } catch (err) {
      toast.error('Tactical failure during offer transmission');
    } finally {
      setIsSendingOffer(false);
    }
  };

  const onSave = async () => {
    let lat = 30.2672, lng = -97.7431;
    if (formData.propertyAddress) { 
      try {
        const c = await geocodeAddress(formData.propertyAddress); 
        if (c) { lat = c.lat; lng = c.lng; } 
      } catch (err) {}
    } else if (formData.draw_polygon && formData.draw_polygon.length > 0) {
      lat = formData.draw_polygon[0][0];
      lng = formData.draw_polygon[0][1];
    }
    const d: any = { 
      name: formData.name, 
      email: formData.email, 
      phone: formData.phone, 
      propertyAddress: formData.propertyAddress, 
      propertyType: formData.propertyType, 
      estimatedValue: parseFloat(formData.estimatedValue) || 0, 
      offerAmount: parseFloat(formData.offerAmount) || 0, 
      status: formData.status as LeadStatus, 
      notes: formData.notes, 
      assignedTo: formData.assignedTo,
      lat, lng, 
      source: formData.source || 'other', 
      probability: parseInt(formData.probability), 
      engagementLevel: parseInt(formData.engagementLevel), 
      timelineUrgency: parseInt(formData.timelineUrgency), 
      competitionLevel: parseInt(formData.competitionLevel),
      lastSoldPrice: parseFloat(formData.lastSoldPrice) || null,
      lastSoldDate: formData.lastSoldDate || null,
      estimatedEquity: parseFloat(formData.estimatedEquity) || null,
      recommendedOffer: parseFloat(formData.recommendedOffer) || null,
      lead_type: formData.lead_type,
      budget_min: parseFloat(formData.budget_min) || null,
      budget_max: parseFloat(formData.budget_max) || null,
      desired_bedrooms: parseFloat(formData.desired_bedrooms) || null,
      desired_bathrooms: parseFloat(formData.desired_bathrooms) || null,
      preferred_property_types: formData.preferred_property_types,
      preferred_locations: formData.preferred_locations,
      pre_approved: formData.pre_approved,
      lender_info: formData.lender_info,
      draw_polygon: formData.draw_polygon,
    };

    let result;
    if (editingLead) {
      result = await updateLead(editingLead.id, d);
      if (d.phone && d.phone !== editingLead.phone) {
        detectCarrier(d.phone).then(res => {
          if (res.carrier) updateLead(editingLead.id, { carrier: res.carrier });
        });
      }
    } else {
      const res = await detectCarrier(d.phone);
      result = await addLead({ ...d, carrier: res.carrier });
    }

    if (result && !result.success) {
      throw new Error(result.error?.message || 'Unknown error');
    }
    
    setTimeout(() => onClose(), 1000);
  };

  const scoreBadge = (s: number) => {
    if (s >= 80) return { className: 'border-emerald-500/40', style: { backgroundColor: 'rgba(16, 185, 129, 0.25)', color: '#10b981' } };

    if (s >= 60) return { className: 'border-blue-500/40', style: { backgroundColor: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6' } };
    if (s >= 40) return { className: 'border-amber-500/40', style: { backgroundColor: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' } };
    if (s >= 20) return { className: 'border-orange-500/40', style: { backgroundColor: 'rgba(249, 115, 22, 0.15)', color: '#f97316' } };
    return { className: 'border-rose-500/40', style: { backgroundColor: 'rgba(244, 63, 94, 0.15)', color: '#f43f5e' } };
  };

  return (
    <>
    <Modal 
      isOpen={isOpen} 
      onClose={handleCloseAttempt}
      title={editingLead ? 'Edit Lead' : 'Add New Lead'}
      maxWidth="max-w-3xl"
    >
      <form onSubmit={(e) => e.preventDefault()} className="p-6 space-y-6 max-h-[85vh] overflow-y-auto custom-scrollbar">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-bold text-[var(--t-text-muted)] mb-2 uppercase tracking-widest">Name *</label>
            <input 
              type="text" 
              value={formData.name} 
              onChange={e => setFormData({ ...formData, name: e.target.value })} 
              required 
              placeholder="e.g. John Smith"
              className="w-full px-4 py-3 bg-[var(--t-surface-dim)] border border-[var(--t-border)] rounded-2xl text-white focus:outline-none focus:border-[var(--t-primary)] transition-all" 
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-[var(--t-text-muted)] mb-2 uppercase tracking-widest">Email</label>
            <input 
              type="email" 
              value={formData.email} 
              onChange={e => setFormData({ ...formData, email: e.target.value })} 
              placeholder="smith@example.com"
              className="w-full px-4 py-3 bg-[var(--t-surface-dim)] border border-[var(--t-border)] rounded-2xl text-white focus:outline-none focus:border-[var(--t-primary)] transition-all" 
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-[var(--t-text-muted)] mb-2 uppercase tracking-widest">Phone</label>
            <input 
              type="tel" 
              value={formData.phone} 
              onChange={e => setFormData({ ...formData, phone: e.target.value })} 
              placeholder="(555) 123-4567"
              className="w-full px-4 py-3 bg-[var(--t-surface-dim)] border border-[var(--t-border)] rounded-2xl text-white focus:outline-none focus:border-[var(--t-primary)] transition-all" 
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-bold text-[var(--t-text-muted)] mb-2 uppercase tracking-widest">Lead Type</label>
            <div className="flex items-center bg-[var(--t-surface-hover)] rounded-2xl p-1 border border-[var(--t-border)] w-fit">
              {(['seller', 'buyer', 'both'] as const).map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, lead_type: t }))}
                  className={`px-5 py-2.5 rounded-xl text-xs font-semibold capitalize transition-all ${
                    formData.lead_type === t
                      ? 'bg-[var(--t-primary)] text-white shadow-lg shadow-[var(--t-primary-dim)]'
                      : 'text-[var(--t-text-muted)] hover:text-white'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Seller / Property Address - Only if seller or both */}
          {(formData.lead_type === 'seller' || formData.lead_type === 'both') && (
            <>
              <div className="md:col-span-2 pt-4 border-t border-[var(--t-border-subtle)]">
                <h3 className="text-xs font-black text-[var(--t-primary)] uppercase tracking-[0.2em] mb-4">Property & Logistics</h3>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-[var(--t-text-muted)] mb-2 uppercase tracking-widest">Property Address</label>
                <input 
                  value={formData.propertyAddress} 
                  onChange={e => setFormData({ ...formData, propertyAddress: e.target.value })} 
                  onBlur={handleAddressBlur}
                  placeholder="123 Main St, Austin, TX"
                  className="w-full px-4 py-3 bg-[var(--t-surface-dim)] border border-[var(--t-border)] rounded-2xl text-white focus:outline-none focus:border-[var(--t-primary)] transition-all" 
                />
                {estimateDetails && (
                  <div className="mt-2 text-[11px] text-[var(--t-text-muted)] flex flex-col sm:flex-row sm:items-center gap-1.5 px-3 py-1.5 bg-[var(--t-surface)] border border-[var(--t-border)] rounded-xl">
                    <span className="font-semibold text-white">Estimated Value: ${estimateDetails.estimate.toLocaleString()}</span>
                    <span className="hidden sm:inline opacity-50">|</span>
                    <span>Source: {estimateDetails.source}</span>
                    <span className="hidden sm:inline opacity-50">|</span>
                    <span className="italic">{estimateDetails.disclaimer}</span>
                  </div>
                )}
              </div>
            </>
          )}
          {/* General details - Always shown */}
          <div>
            <label className="block text-sm font-bold text-[var(--t-text-muted)] mb-2 uppercase tracking-widest">Status</label>
            <select 
              value={formData.status} 
              onChange={e => setFormData({ ...formData, status: e.target.value })} 
              className="w-full px-4 py-3 bg-[var(--t-surface-dim)] border border-[var(--t-border)] rounded-2xl text-[var(--t-text)] focus:outline-none focus:border-[var(--t-primary)] transition-all appearance-none"
              style={{ backgroundColor: 'var(--t-surface)', color: 'var(--t-primary)', borderColor: 'var(--t-border)' }}
            >
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="qualified">Qualified</option>
              <option value="negotiating">Negotiating</option>
              <option value="closed-won">Closed Won</option>
              <option value="closed-lost">Closed Lost</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-bold text-[var(--t-text-muted)] mb-2 uppercase tracking-widest">Assigned To</label>
            <select 
              value={formData.assignedTo} 
              onChange={e => setFormData({ ...formData, assignedTo: e.target.value })} 
              className="w-full px-4 py-3 bg-[var(--t-surface-dim)] border border-[var(--t-border)] rounded-2xl text-[var(--t-text)] focus:outline-none focus:border-[var(--t-primary)] transition-all appearance-none"
              style={{ backgroundColor: 'var(--t-surface)', color: 'var(--t-primary)', borderColor: 'var(--t-border)' }}
            >
              <option value="">Unassigned</option>
              {team.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>

          {/* Seller specific financial details */}
          {(formData.lead_type === 'seller' || formData.lead_type === 'both') && (
            <div className="md:col-span-2 pt-4 border-t border-[var(--t-border-subtle)]">
              <h3 className="text-xs font-black text-[var(--t-success)] uppercase tracking-[0.2em] mb-4">Financial Details</h3>
            </div>
          )}

          {/* Seller Specific Financial Inputs */}
          {(formData.lead_type === 'seller' || formData.lead_type === 'both') && (
            <>
              <div>
                <label className="block text-sm font-bold text-[var(--t-text-muted)] mb-2 uppercase tracking-widest">Estimated Value</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--t-text-secondary)] font-bold">$</span>
                  <input 
                    type="number" 
                    value={formData.estimatedValue} 
                    onChange={e => setFormData({ ...formData, estimatedValue: e.target.value })} 
                    className="w-full pl-8 pr-4 py-3 bg-[var(--t-surface-dim)] border border-[var(--t-border)] rounded-2xl text-white focus:outline-none focus:border-[var(--t-primary)] transition-all" 
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-[var(--t-text-muted)] mb-2 uppercase tracking-widest">Last Sold Price</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--t-text-secondary)] font-bold">$</span>
                  <input 
                    type="number" 
                    value={formData.lastSoldPrice} 
                    onChange={e => setFormData({ ...formData, lastSoldPrice: e.target.value })} 
                    className="w-full pl-8 pr-4 py-3 bg-[var(--t-surface-dim)] border border-[var(--t-border)] rounded-2xl text-white focus:outline-none focus:border-[var(--t-primary)] transition-all" 
                  />
                  {appreciationPercent !== 0 && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
                      <TrendingUp size={12} className={appreciationPercent > 0 ? 'text-[var(--t-success)]' : 'text-[var(--t-error)]'} />
                      <span className={`text-[10px] font-black ${appreciationPercent > 0 ? 'text-[var(--t-success)]' : 'text-[var(--t-error)]'}`}>
                        {appreciationPercent}%
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-[var(--t-text-muted)] mb-2 uppercase tracking-widest">Last Sold Date</label>
                <div className="relative">
                  <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--t-text-secondary)]" size={16} />
                  <input 
                    type="date" 
                    value={formData.lastSoldDate} 
                    onChange={e => setFormData({ ...formData, lastSoldDate: e.target.value })} 
                    className="w-full pl-11 pr-4 py-3 bg-[var(--t-surface-dim)] border border-[var(--t-border)] rounded-2xl text-white focus:outline-none focus:border-[var(--t-primary)] transition-all appearance-none" 
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-[var(--t-text-muted)] mb-2 uppercase tracking-widest">Estimated Equity</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--t-text-secondary)] font-bold">$</span>
                  <input 
                    type="number" 
                    value={formData.estimatedEquity} 
                    onChange={e => setFormData({ ...formData, estimatedEquity: e.target.value })} 
                    className="w-full pl-8 pr-4 py-3 bg-[var(--t-surface-dim)] border border-[var(--t-border)] rounded-2xl text-white focus:outline-none focus:border-[var(--t-primary)] transition-all" 
                  />
                </div>
              </div>
            </>
          )}

          {/* Seller specific offer calculator */}
          {(formData.lead_type === 'seller' || formData.lead_type === 'both') && (
            <>
              <div className="md:col-span-2 p-1 bg-[var(--t-surface-subtle)] rounded-[20px]">
                <div className="bg-[var(--t-surface-dim)] p-4 rounded-[18px] border border-[var(--t-border-subtle)] flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[var(--t-success-dim)] flex items-center justify-center">
                      <Calculator className="text-[var(--t-success)]" size={20} />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-[var(--t-text-muted)] uppercase tracking-wider">Recommended Offer</p>
                      <p className="text-lg font-black text-white">{formData.recommendedOffer ? `$${parseInt(formData.recommendedOffer).toLocaleString()}` : '$0'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleRecommendOffer}
                      disabled={!formData.estimatedValue}
                      className="px-4 py-2 bg-[var(--t-surface-dim)] text-[var(--t-text)] text-xs font-black rounded-lg border border-[var(--t-border)] hover:bg-[var(--t-surface-hover)] transition-all disabled:opacity-50"
                    >
                      Calc
                    </button>
                    <button
                      type="button"
                      onClick={handleSendOffer}
                      disabled={!formData.offerAmount || isSendingOffer}
                      className="px-4 py-2 bg-[var(--t-primary)] text-white text-xs font-black rounded-lg hover:brightness-110 active:scale-95 transition-all disabled:opacity-50 shadow-lg shadow-[var(--t-primary-dim)] flex items-center gap-2"
                    >
                      {isSendingOffer ? (
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <Send size={14} />
                      )}
                      Send Offer
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-[var(--t-text-muted)] mb-2 uppercase tracking-widest">Final Offer Amount</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--t-text-secondary)] font-bold">$</span>
                  <input 
                    type="number" 
                    value={formData.offerAmount} 
                    onChange={e => setFormData({ ...formData, offerAmount: e.target.value })} 
                    className="w-full pl-8 pr-4 py-3 bg-[var(--t-surface-dim)] border border-[var(--t-border)] rounded-2xl text-white focus:outline-none focus:border-[var(--t-primary)] transition-all" 
                  />
                </div>
              </div>
            </>
          )}

          {/* Buyer specifications - Only if buyer or both */}
          {(formData.lead_type === 'buyer' || formData.lead_type === 'both') && (
            <>
              <div className="md:col-span-2 pt-4 border-t border-[var(--t-border-subtle)]">
                <h3 className="text-xs font-black text-[var(--t-primary)] uppercase tracking-[0.2em] mb-4">Buyer Specifications</h3>
              </div>

              <div>
                <label className="block text-sm font-bold text-[var(--t-text-muted)] mb-2 uppercase tracking-widest">Budget Min</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--t-text-secondary)] font-bold">$</span>
                  <input 
                    type="number" 
                    value={formData.budget_min} 
                    onChange={e => setFormData({ ...formData, budget_min: e.target.value })} 
                    placeholder="e.g. 200000"
                    className="w-full pl-8 pr-4 py-3 bg-[var(--t-surface-dim)] border border-[var(--t-border)] rounded-2xl text-white focus:outline-none focus:border-[var(--t-primary)] transition-all" 
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-[var(--t-text-muted)] mb-2 uppercase tracking-widest">Budget Max</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--t-text-secondary)] font-bold">$</span>
                  <input 
                    type="number" 
                    value={formData.budget_max} 
                    onChange={e => setFormData({ ...formData, budget_max: e.target.value })} 
                    placeholder="e.g. 500000"
                    className="w-full pl-8 pr-4 py-3 bg-[var(--t-surface-dim)] border border-[var(--t-border)] rounded-2xl text-white focus:outline-none focus:border-[var(--t-primary)] transition-all" 
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-[var(--t-text-muted)] mb-2 uppercase tracking-widest">Desired Bedrooms</label>
                <input 
                  type="number" 
                  value={formData.desired_bedrooms} 
                  onChange={e => setFormData({ ...formData, desired_bedrooms: e.target.value })} 
                  placeholder="e.g. 3"
                  className="w-full px-4 py-3 bg-[var(--t-surface-dim)] border border-[var(--t-border)] rounded-2xl text-white focus:outline-none focus:border-[var(--t-primary)] transition-all" 
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-[var(--t-text-muted)] mb-2 uppercase tracking-widest">Desired Bathrooms</label>
                <input 
                  type="number" 
                  value={formData.desired_bathrooms} 
                  onChange={e => setFormData({ ...formData, desired_bathrooms: e.target.value })} 
                  placeholder="e.g. 2"
                  className="w-full px-4 py-3 bg-[var(--t-surface-dim)] border border-[var(--t-border)] rounded-2xl text-white focus:outline-none focus:border-[var(--t-primary)] transition-all" 
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-[var(--t-text-muted)] mb-2 uppercase tracking-widest">Preferred Property Types</label>
                <div className="flex flex-wrap gap-2">
                  {['single-family', 'multi-family', 'commercial', 'land', 'condo'].map(type => {
                    const selected = formData.preferred_property_types.includes(type);
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            preferred_property_types: selected
                              ? prev.preferred_property_types.filter(t => t !== type)
                              : [...prev.preferred_property_types, type]
                          }));
                        }}
                        className={`px-4 py-2 rounded-xl text-xs font-semibold capitalize border transition-all ${
                          selected
                            ? 'bg-[var(--t-primary-dim)] text-[var(--t-primary)] border-[var(--t-primary)]'
                            : 'bg-[var(--t-surface-dim)] text-[var(--t-text-muted)] border-[var(--t-border)]'
                        }`}
                      >
                        {type.replace('-', ' ')}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-[var(--t-text-muted)] mb-2 uppercase tracking-widest">Preferred Locations (Zip Codes)</label>
                <input 
                  type="text" 
                  value={formData.preferred_locations.join(', ')} 
                  onChange={e => setFormData({ ...formData, preferred_locations: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} 
                  placeholder="e.g. 78701, 78704"
                  className="w-full px-4 py-3 bg-[var(--t-surface-dim)] border border-[var(--t-border)] rounded-2xl text-white focus:outline-none focus:border-[var(--t-primary)] transition-all" 
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-[var(--t-text-muted)] mb-2 uppercase tracking-widest flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    checked={formData.pre_approved} 
                    onChange={e => setFormData({ ...formData, pre_approved: e.target.checked })} 
                    className="w-4 h-4 rounded border-[var(--t-border)] bg-[var(--t-surface-dim)] text-[var(--t-primary)] focus:ring-[var(--t-primary)]" 
                  />
                  Pre-approved Status
                </label>
              </div>
              <div>
                {formData.pre_approved && (
                  <>
                    <label className="block text-sm font-bold text-[var(--t-text-muted)] mb-2 uppercase tracking-widest">Lender Information</label>
                    <input 
                      type="text" 
                      value={formData.lender_info} 
                      onChange={e => setFormData({ ...formData, lender_info: e.target.value })} 
                      placeholder="e.g. Chase Bank, Loan Officer name"
                      className="w-full px-4 py-3 bg-[var(--t-surface-dim)] border border-[var(--t-border)] rounded-2xl text-white focus:outline-none focus:border-[var(--t-primary)] transition-all" 
                    />
                  </>
                )}
              </div>

              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-bold text-[var(--t-text-muted)] uppercase tracking-widest">Draw Search Area on Map</label>
                  {formData.draw_polygon && formData.draw_polygon.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, draw_polygon: null })}
                      className="text-xs text-[var(--t-error)] hover:underline"
                    >
                      Clear Drawing
                    </button>
                  )}
                </div>
                <div className="h-60 w-full rounded-2xl overflow-hidden border border-[var(--t-border)] relative">
                  <MapContainer
                    center={[30.2672, -97.7431]}
                    zoom={12}
                    className="h-full w-full animate-fade-in"
                    scrollWheelZoom={true}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                      url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                    />
                    <LeadFormMap 
                      points={formData.draw_polygon || []} 
                      onChange={(pts) => setFormData(prev => ({ ...prev, draw_polygon: pts.length > 0 ? pts : null }))} 
                    />
                  </MapContainer>
                </div>
                <span className="text-[10px] text-[var(--t-text-muted)] mt-1.5 block">
                  Click on the map to define the corners of the buyer's search area polygon. Click on a marker to remove it.
                </span>
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-bold text-[var(--t-text-muted)] mb-2 uppercase tracking-widest">Lead Source</label>
            <select 
              value={formData.source} 
              onChange={e => setFormData({ ...formData, source: e.target.value as LeadSource })} 
              className="w-full px-4 py-3 bg-[var(--t-surface-dim)] border border-[var(--t-border)] rounded-2xl text-[var(--t-text)] focus:outline-none focus:border-[var(--t-primary)] transition-all appearance-none"
              style={{ backgroundColor: 'var(--t-surface)', color: 'var(--t-primary)', borderColor: 'var(--t-border)' }}
            >
              <option value="bandit-signs">Bandit Signs</option>
              <option value="personal-relations">Personal Relations</option>
              <option value="pay-per-lead">Pay Per Lead</option>
              <option value="doorknocking">Doorknocking</option>
              <option value="referral">Referral</option>
              <option value="website">Website</option>
              <option value="social-media">Social Media</option>
              <option value="open-house">Open House</option>
              <option value="fsbo">FSBO</option>
              <option value="cold-call">Cold Call</option>
              <option value="email-campaign">Email Campaign</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-bold text-[var(--t-text-muted)] mb-2 uppercase tracking-widest">Notes</label>
            <textarea 
              value={formData.notes} 
              onChange={e => setFormData({ ...formData, notes: e.target.value })} 
              rows={4} 
              placeholder="Add any additional context here..."
              className="w-full px-4 py-3 bg-[var(--t-surface-dim)] border border-[var(--t-border)] rounded-2xl text-white focus:outline-none focus:border-[var(--t-primary)] transition-all resize-none" 
            />
          </div>
        </div>
        
        <div className="bg-[var(--t-surface-dim)] p-6 rounded-[24px] border border-[var(--t-border)]">
          <h3 className="text-[var(--t-text)] font-bold mb-4 flex items-center gap-2 uppercase tracking-widest text-xs">
            <BarChart3 className="w-4 h-4 text-[var(--t-primary)]" /> Deal Score Parameters
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-[10px] font-bold text-[var(--t-text-muted)] mb-2 uppercase tracking-widest">Probability %</label>
              <input 
                type="number" 
                min="0" 
                max="100" 
                value={formData.probability} 
                onChange={e => setFormData({ ...formData, probability: e.target.value })} 
                className="w-full px-3 py-2 bg-[var(--t-surface)] border border-[var(--t-border)] rounded-xl text-white focus:outline-none focus:border-[var(--t-primary)]" 
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-[var(--t-text-muted)] mb-2 uppercase tracking-widest">Engagement (1-5)</label>
              <input 
                type="number" 
                min="1" 
                max="5" 
                value={formData.engagementLevel} 
                onChange={e => setFormData({ ...formData, engagementLevel: e.target.value })} 
                className="w-full px-3 py-2 bg-[var(--t-surface)] border border-[var(--t-border)] rounded-xl text-white focus:outline-none focus:border-[var(--t-primary)]" 
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-[var(--t-text-muted)] mb-2 uppercase tracking-widest">Urgency (1-5)</label>
              <input 
                type="number" 
                min="1" 
                max="5" 
                value={formData.timelineUrgency} 
                onChange={e => setFormData({ ...formData, timelineUrgency: e.target.value })} 
                className="w-full px-3 py-2 bg-[var(--t-surface)] border border-[var(--t-border)] rounded-xl text-white focus:outline-none focus:border-[var(--t-primary)]" 
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-[var(--t-text-muted)] mb-2 uppercase tracking-widest">Competition (1-5)</label>
              <input 
                type="number" 
                min="1" 
                max="5" 
                value={formData.competitionLevel} 
                onChange={e => setFormData({ ...formData, competitionLevel: e.target.value })} 
                className="w-full px-3 py-2 bg-[var(--t-surface)] border border-[var(--t-border)] rounded-xl text-white focus:outline-none focus:border-[var(--t-primary)]" 
              />
            </div>
          </div>
          <div className="mt-6 flex items-center justify-between bg-[var(--t-background)] p-4 rounded-2xl border border-[var(--t-border)]">
            <span className="text-sm font-bold text-[var(--t-text-muted)] uppercase tracking-widest">Calculated Score Preview</span>
            {(() => {
              const previewScore = calculateDealScore({
                estimatedValue: parseFloat(formData.estimatedValue) || 0,
                probability: parseInt(formData.probability) || 50,
                engagementLevel: parseInt(formData.engagementLevel) || 3,
                timelineUrgency: parseInt(formData.timelineUrgency) || 3,
                competitionLevel: parseInt(formData.competitionLevel) || 3
              } as any);
              const sb = scoreBadge(previewScore);
              return (
                <span className={`px-4 py-1.5 rounded-xl text-sm font-black border transition-all ${sb.className}`} style={sb.style}>
                  ⚡ {previewScore}
                </span>
              );
            })()}
          </div>
        </div>
        
        <div className="flex justify-end gap-3 pt-4 border-t border-[var(--t-border)]">
          <button 
            type="button" 
            onClick={handleCloseAttempt} 
            className="px-6 py-3 bg-[var(--t-surface-subtle)] hover:bg-[var(--t-surface-hover)] rounded-2xl text-[var(--t-text)] font-bold transition-all"
          >
            Cancel
          </button>
          <SaveButton 
            onSave={onSave}
            disabled={!formData.name}
            label={editingLead ? 'Update Lead' : 'Create Lead'}
          />
        </div>
      </form>
    </Modal>
    
    <ConfirmModal 
      isOpen={showDiscardConfirm}
      onClose={() => setShowDiscardConfirm(false)}
      onConfirm={() => {
        setShowDiscardConfirm(false);
        onClose();
      }}
      title="Discard Changes?"
      message="You have unsaved changes. Are you sure you want to discard them? Any progress will be lost."
      confirmLabel="Discard"
      cancelLabel="Cancel"
      variant="danger"
    />
  </>
  );
}

